import React, { useEffect, useState, useCallback } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { isShoppingMode } from '../../config/appMode';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import { canCancelBid, getBidCancelDisabledReason } from '../../utils/bidCancelUtils';
import { parseApiDateTime } from '../../utils/dateUtils';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import PrimaryButton from '../../components/PrimaryButton';
import NoDataContainer from '../../components/NoDataContainer';
import DataLoader from '../../components/DataLoader';
import Modal from '../../components/Modal';
import LotCard from '../../customComponents/FavoriteCard';
import { getLikedLots, likeLot, cancelPreBid, getBidHistory } from '../../lib/api/user/userApi';
import { LikedLotData } from '../../lib/api/user/userApi.types';
import { AppColors } from '../../styles/colors';

const FavoritesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: calc(100vh - 120px); /* 앱바와 바텀바 높이를 제외한 나머지 공간 */
`;

// 즐겨찾기 카드 컨테이너 (그리드)
const FavoritesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
    padding: 32px 0;
  }
`;

// 모달 버튼 스타일
const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const CancelModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: ${AppColors.btnC};
  color: ${AppColors.onBtnC};
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const ConfirmModalButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: ${AppColors.btnCEmphasis};
  color: ${AppColors.onBtnCEmphasis};
  
  &:hover {
    background-color: #262626;
    color: #FFFFFF;
  }
`;

// 즐겨찾기 아이템 타입은 API 응답 타입을 그대로 사용
type FavoriteItem = LikedLotData;

// 다국어 텍스트
const favoritesTexts = {
  ko: {
    title: '즐겨찾기',
    preBid: isShoppingMode() ? '구매하기' : '입찰하기',
    preSubmission: isShoppingMode() ? '구매하기' : '사전 입찰하기',
    liveView: '라이브참여하기',
    liveBid: isShoppingMode() ? '구매하기' : (process.env.NODE_ENV === 'development' ? '경매등록하기' : '라이브입찰하기'),
    cancelBid: isShoppingMode() ? '구매취소' : '입찰취소',
    cancelBidBlockedTitle: isShoppingMode() ? '구매취소 불가' : '입찰취소 불가',
    emptyTitle: '즐겨찾기가 비어있어요',
    emptyDescription: '관심있는 작품을 즐겨찾기에 추가해보세요.',
    noFavoritesMessage: '즐겨찾기 내역이 없습니다',
    photoSubmissionDate: isShoppingMode() ? '판매시작' : '사전입찰',
    auctionStart: isShoppingMode() ? '판매시작' : '경매시작',
    auctionEnded: isShoppingMode() ? '판매종료' : '경매종료',
    auctionNotStarted: isShoppingMode() ? '판매 시작 전입니다' : '경매 시작 전입니다',
    auctionEndedTitle: isShoppingMode() ? '판매가 종료되었습니다' : '경매가 종료되었습니다',
    auctionEndedMessage: isShoppingMode() ? '이 판매는 이미 종료되었습니다.' : '이 경매는 이미 종료되었습니다.',
    confirmButton: '확인',
    liveStartPrice: isShoppingMode() ? '판매가' : '라이브 시작가',
    startPrice: isShoppingMode() ? '판매가' : '시작가',
    // 모달 관련 텍스트들
    cancelBidModalTitle: isShoppingMode() ? '구매를 취소하시겠습니까?' : '입찰을 취소하시겠습니까?',
    cancelBidModalMessage: isShoppingMode() ? '취소 후에는 해당 구매 내역이 무효 처리되며, 다시 복구할 수 없습니다.' : '취소 후에는 해당 입찰 내역이 무효 처리되며, 다시 복구할 수 없습니다.',
    cancel: '취소',
    confirmCancel: isShoppingMode() ? '구매취소' : '입찰취소'
  },
  en: {
    title: 'Favorites',
    preBid: isShoppingMode() ? 'Purchase' : 'Bid',
    preSubmission: isShoppingMode() ? 'Purchase' : 'Pre-Bid',
    liveView: 'Live View',
    liveBid: isShoppingMode() ? 'Purchase' : (process.env.NODE_ENV === 'development' ? 'Live Bid' : 'Place Live Bid'),
    cancelBid: isShoppingMode() ? 'Cancel Purchase' : 'Cancel Bid',
    cancelBidBlockedTitle: isShoppingMode() ? 'Cancellation Unavailable' : 'Cancellation Unavailable',
    emptyTitle: 'No favorites yet',
    emptyDescription: 'Add artworks you\'re interested in to your favorites.',
    noFavoritesMessage: 'No favorites found',
    photoSubmissionDate: isShoppingMode() ? 'Sale Start' : 'Pre-Bid',
    auctionStart: isShoppingMode() ? 'Sale Start' : 'Auction Start',
    auctionEnded: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    auctionNotStarted: isShoppingMode() ? 'Sale Not Started' : 'Auction Not Started',
    auctionEndedTitle: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    auctionEndedMessage: isShoppingMode() ? 'This sale has already ended.' : 'This auction has already ended.',
    confirmButton: 'OK',
    liveStartPrice: isShoppingMode() ? 'Price' : 'Live Starting Price',
    startPrice: isShoppingMode() ? 'Price' : 'Starting Price',
    // 모달 관련 텍스트들
    cancelBidModalTitle: isShoppingMode() ? 'Do you want to cancel your purchase?' : 'Do you want to cancel your bid?',
    cancelBidModalMessage: isShoppingMode() ? 'After cancellation, your purchase will be invalidated and cannot be recovered.' : 'After cancellation, your bid will be invalidated and cannot be recovered.',
    cancel: 'Cancel',
    confirmCancel: isShoppingMode() ? 'Cancel Purchase' : 'Cancel Bid'
  }
};

// 요일 다국어 텍스트
const dayNamesLocalized = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

// 시간 포맷 유틸리티 함수
const formatLiveTime = (liveTime: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const targetDate = new Date(liveTime);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    // 현재 시간이 라이브 시간 이후라면 "LIVE"
    if (now >= targetDate) {
      return 'LIVE';
    }
    
    // 오늘이고 미래 시간이면 남은 시간 계산 (xxh xxm 형태)
    if (targetDay.getTime() === today.getTime()) {
      const diffMs = targetDate.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    }
    
    // 오늘이 아니면 날짜 형태로 표시 (M/d(요일) HH:mm)
    const month = targetDate.getMonth() + 1;
    const date = targetDate.getDate();
    const dayNames = dayNamesLocalized[language];
    const dayName = dayNames[targetDate.getDay()];
    const hours = targetDate.getHours().toString().padStart(2, '0');
    const minutes = targetDate.getMinutes().toString().padStart(2, '0');
    
    return `${month}/${date}(${dayName}) ${hours}:${minutes}`;
  } catch (error) {
    return liveTime; // 오류 발생시 원본 반환
  }
};



// 날짜를 로컬 형식으로 포맷팅하는 함수
const formatDateToLocalString = (date: Date, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const year = date.getFullYear().toString().slice(-2); // yy
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mm
    const day = date.getDate().toString().padStart(2, '0'); // dd
    const hours = date.getHours().toString().padStart(2, '0'); // hh
    const minutes = date.getMinutes().toString().padStart(2, '0'); // MM
    const dayName = dayNamesLocalized[language][date.getDay()]; // 요일
    
    return language === 'ko' 
      ? `${year}.${month}.${day} (${dayName}) ${hours}:${minutes}`
      : `${hours}:${minutes} on ${month}/${day}/${year} (${dayName})`;
  } catch (error) {
    return date.toString();
  }
};

// LiveEndTime 포맷팅 함수 (07/17(금) 13:00 종료 / Jul 17(Fri) 13:00 Ends)
const formatLiveEndTime = (dateStr: string, language: 'ko' | 'en' = 'ko'): string => {
  try {
    const date = new Date(dateStr);
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mm
    const day = date.getDate(); // d (패딩 없음)
    const hours = date.getHours().toString().padStart(2, '0'); // hh
    const minutes = date.getMinutes().toString().padStart(2, '0'); // MM
    const dayName = dayNamesLocalized[language][date.getDay()]; // 요일
    
    if (language === 'ko') {
      return `${month}/${day}(${dayName}) ${hours}:${minutes} 종료`;
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[date.getMonth()];
      return `${monthName} ${day}(${dayName}) ${hours}:${minutes} Ends`;
    }
  } catch (error) {
    return dateStr;
  }
};

// KRW 가격 포맷팅 함수 (숫자를 KRW 1,000,000 형태로 변환) - 국제화 지원
const formatPrice = (price: number, language: 'ko' | 'en' = 'ko', exchangeRate: number = 1300): string => {
  if (language === 'ko') {
    return `KRW ${price.toLocaleString()}`;
  } else {
    const usdPrice = price / exchangeRate;
    return `USD ${usdPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

// 경매 상태 확인 함수 - 실제 API 데이터 구조에 맞춤
const getAuctionStatus = (item: FavoriteItem): 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended' => {
  const now = new Date();
  
  // auction 객체에서 시간 정보 추출
  if (!item.auction) return 'ended';
  
  const preBidStartAt = parseApiDateTime(item.auction.preBidStartAt);
  const preBidEndAt = parseApiDateTime(item.auction.preBidEndAt);
  const auctionStartAt = parseApiDateTime(item.auction.auctionStartAt);
  const auctionEndAt = parseApiDateTime(item.auction.auctionEndAt);

  if (!preBidStartAt || !preBidEndAt || !auctionStartAt || !auctionEndAt) return 'ended';
  
  // 시간 기준으로 상태 판단
  if (now > auctionEndAt) return 'ended';
  if (now < preBidStartAt) return 'before-submission';
  if (now >= preBidStartAt && now < preBidEndAt) return 'submission-period';
  if (now >= preBidEndAt && now < auctionStartAt) return 'before-live';
  return 'live';
};

function Favorites() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUserAuth();
  const { lang } = useLang();
  const texts = favoritesTexts[lang];
  
  // API 상태 관리
  const [favoriteItems, setFavoriteItems] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exchangeRate] = useState(1300); // 환율 기본값
  
  // 사용자 입찰 내역 상태
  const [userBidLots, setUserBidLots] = useState<Set<number>>(new Set());

  // 모달 상태 관리
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    buttonText: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    buttonText: ''
  });

  // 입찰 취소 모달 상태 관리
  const [cancelBidModalState, setCancelBidModalState] = useState<{
    isOpen: boolean;
    selectedItem: FavoriteItem | null;
    mode: 'confirm' | 'blocked';
    blockedReason: string;
  }>({
    isOpen: false,
    selectedItem: null,
    mode: 'confirm',
    blockedReason: ''
  });

  // 사용자 입찰 내역 조회
  const fetchUserBidHistory = useCallback(async () => {
    if (!isLoggedIn) return;

    try {
      const response = await getBidHistory({
        page: 1,
        limit: 1000, // 모든 입찰 내역 가져오기
        status: 'all'
      });
      
      if (response.data.statusCode === 200 && response.data.data) {
        const bidLotIds = new Set<number>();
        response.data.data.forEach((bid: any) => {
          if (bid.lot?.index) {
            bidLotIds.add(bid.lot.index);
          }
        });
        setUserBidLots(bidLotIds);
        devLog('사용자 입찰 LOT IDs:', Array.from(bidLotIds));
      }
    } catch (error) {
      devError('입찰 내역 조회 실패:', error);
    }
  }, [isLoggedIn]);

  // 좋아요한 작품 목록 조회
  const fetchLikedLots = useCallback(async () => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getLikedLots();
      devLog('좋아요한 작품 목록:', response);
      
      // API 응답에서 data는 배열 형태
      setFavoriteItems(response.data?.data || []);
    } catch (error) {
      devError('좋아요한 작품 조회 실패:', error);
      setError('좋아요한 작품을 불러오는데 실패했습니다.');
      setFavoriteItems([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // 컴포넌트 마운트 시 데이터 조회
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchLikedLots(),
        fetchUserBidHistory()
      ]);
    };
    
    loadData();
  }, [fetchLikedLots, fetchUserBidHistory]);

  // 사용자가 해당 LOT에 사전입찰을 했는지 확인하는 함수
  const hasUserPreBid = (item: FavoriteItem): boolean => {
    return userBidLots.has(item.index);
  };

  // 현재 LOT에서 사용자의 사전입찰 순위 (1위, 2위 등) 계산
  const getUserPreBidRank = (item: FavoriteItem): number | null => {
    const rawPreBids = (item as any).preBids;
    const rawBids = (item as any).bids;
    const extractBidArray = (value: any): any[] => {
      if (Array.isArray(value)) return value;
      if (value && typeof value === 'object') {
        if (Array.isArray(value.data)) return value.data;
        if (Array.isArray(value.items)) return value.items;
      }
      return [];
    };

    const bidList: any[] = [
      ...extractBidArray(rawPreBids),
      ...extractBidArray(rawBids)
    ];

    if (!user?.index || bidList.length === 0) return null;

    const userMaxPriceMap = new Map<number, number>();

    bidList.forEach((bid: any) => {
      const userIndex = (
        bid.user?.index ??
        bid.userIndex ??
        bid.createBy ??
        bid.create_by
      ) as number | undefined;
      const price = Number(bid.price ?? bid.amount ?? 0);
      if (!userIndex || Number.isNaN(price) || price <= 0) return;

      const previousPrice = userMaxPriceMap.get(userIndex);
      if (previousPrice === undefined || price > previousPrice) {
        userMaxPriceMap.set(userIndex, price);
      }
    });

    if (!userMaxPriceMap.size) return null;

    const sorted = Array.from(userMaxPriceMap.entries()).sort((a, b) => b[1] - a[1]);
    const rankIndex = sorted.findIndex(([userIndex]) => userIndex === user.index);

    if (rankIndex === -1) return null;

    return rankIndex + 1;
  };

  // 입찰 취소 핸들러
  const handleCancelBid = (item: FavoriteItem) => {
    devLog('입찰 취소 요청:', item);
    
    // 입찰 취소 가능 여부 확인
    const auctionStatus = getAuctionStatus(item);

    const canCancel = !!item.auction?.auctionStartAt && canCancelBid(item.auction.auctionStartAt, auctionStatus);

    if (!canCancel) {
      const blockedReason = getBidCancelDisabledReason(item.auction?.auctionStartAt || '', auctionStatus, lang);
      setCancelBidModalState({
        isOpen: true,
        selectedItem: null,
        mode: 'blocked',
        blockedReason
      });
      return;
    }

    setCancelBidModalState({
      isOpen: true,
      selectedItem: item,
      mode: 'confirm',
      blockedReason: ''
    });
  };

  // 입찰 취소 확인 핸들러
  const handleConfirmCancelBid = async () => {
    if (!cancelBidModalState.selectedItem) return;
    
    try {
      devLog('입찰 취소 API 호출 시작:', cancelBidModalState.selectedItem);
      
      // LOT index만 확인 (정확한 값만 사용)
      const selectedItem = cancelBidModalState.selectedItem;
      const lotId = selectedItem.index;
      
      devLog('추출된 lotId:', lotId);
      
      if (!lotId) {
        devError('LOT index를 찾을 수 없습니다:', selectedItem);
        return;
      }
      
      // 추가 방어 로직: API 호출 직전에 다시 한 번 확인
      const auctionStatus = getAuctionStatus(selectedItem);
      
      if (!selectedItem.auction?.auctionStartAt || !canCancelBid(selectedItem.auction.auctionStartAt, auctionStatus)) {
        const disabledReason = getBidCancelDisabledReason(selectedItem.auction?.auctionStartAt || '', auctionStatus, lang);

        // 확인 직전에 조건이 바뀐 경우(시간 경과 등) 사유 모달로 전환
        setCancelBidModalState({
          isOpen: true,
          selectedItem: null,
          mode: 'blocked',
          blockedReason: disabledReason
        });
        return;
      }
      
      // 실제 API 호출로 입찰 취소 처리
      const response = await cancelPreBid(lotId);
      
      devLog('입찰 취소 API 응답:', response);
      
      if (response.data.statusCode === 200) {
        devLog('입찰 취소 성공');
        
        // 모달 닫기
        setCancelBidModalState({
          isOpen: false,
          selectedItem: null,
          mode: 'confirm',
          blockedReason: ''
        });
        
        // 입찰 내역 새로고침
        await fetchUserBidHistory();
      } else {
        devError('입찰 취소 실패:', response.data);
        // 모달 닫기
        setCancelBidModalState({
          isOpen: false,
          selectedItem: null,
          mode: 'confirm',
          blockedReason: ''
        });
      }
    } catch (error) {
      devError('입찰 취소 API 호출 중 오류:', error);
      // 모달 닫기
      setCancelBidModalState({
        isOpen: false,
        selectedItem: null,
        mode: 'confirm',
        blockedReason: ''
      });
    }
  };

  // 입찰 취소 모달 닫기
  const handleCancelBidModalClose = () => {
    setCancelBidModalState({
      isOpen: false,
      selectedItem: null,
      mode: 'confirm',
      blockedReason: ''
    });
  };

  // 카드 클릭 핸들러
  const handleCardClick = (item: FavoriteItem) => {
    const auctionStatus = getAuctionStatus(item);
    
    // 쇼핑 모드일 때는 before-submission과 before-live 상태에서 아무것도 하지 않음
    if (isShoppingMode() && (auctionStatus === 'before-submission' || auctionStatus === 'before-live')) {
      return;
    }
    
    if (auctionStatus === 'before-submission') {
      // 사전 입찰 전: 모달 표시
      const preBidStartTime = new Date(item.auction!.preBidStartAt);
      const startTimeMessage = formatDateToLocalString(preBidStartTime, lang);
      setModalState({
        isOpen: true,
        title: texts.auctionNotStarted,
        message: lang === 'ko' 
          ? `${startTimeMessage}부터 사전입찰이 시작됩니다`
          : `Pre-bidding starts from ${startTimeMessage}`,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'before-live') {
      // 사전입찰 종료 후 라이브 시작 전: "라이브 경매 시작전입니다" 모달
      const auctionStartTime = new Date(item.auction!.auctionStartAt);
      const startTimeMessage = formatDateToLocalString(auctionStartTime, lang);
      setModalState({
        isOpen: true,
        title: lang === 'ko' ? '라이브 경매 시작전입니다' : 'Live Auction Not Started',
        message: lang === 'ko' 
          ? `${startTimeMessage}부터 라이브 경매가 시작됩니다` 
          : `Live auction starts from ${startTimeMessage}`,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'ended') {
      // 경매 종료: 모달 표시
      setModalState({
        isOpen: true,
        title: texts.auctionEndedTitle,
        message: texts.auctionEndedMessage,
        buttonText: texts.confirmButton
      });
    } else if (auctionStatus === 'submission-period') {
      // 사전입찰 기간: 사전입찰 페이지로 이동
      navigate(`/pre-bid/${item.index}`, { state: { selectedItem: item } });
    } else if (auctionStatus === 'live') {
      // 라이브 중: 라이브 페이지로 이동 - LiveBid 페이지가 기대하는 형식으로 데이터 변환
      const transformedItem = {
        ...item,
        apiData: {
          index: item.index,
          auction: item.auction
        },
        auctionIndex: item.auction?.index,
        lotIndex: item.index,
        id: item.index,
        lotId: item.index
      };
      navigate('/live-bid', { state: { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index } });
    }
  };

  // 모달 닫기 함수
  const closeModal = () => {
    setModalState({
      isOpen: false,
      title: '',
      message: '',
      buttonText: ''
    });
  };

  // 좋아요 토글 핸들러
  const handleFavoriteToggle = async (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    
    if (!user) return;

    try {
      await likeLot(index);
      // 성공 시 목록 새로고침
      await fetchLikedLots();
    } catch (error) {
      devError('좋아요 처리 실패:', error);
      alert('좋아요 처리에 실패했습니다.');
    }
  };

  const renderFavoriteCard = (item: FavoriteItem) => {
    const auctionStatus = getAuctionStatus(item);
    const userHasPreBid = hasUserPreBid(item);
    const userPreBidRank = getUserPreBidRank(item);
    const isTopPreBidder = auctionStatus !== 'ended' && userPreBidRank === 1;
    const isSecondPreBidder = auctionStatus !== 'ended' && userPreBidRank === 2;
    
    return (
      <LotCard
        key={item.index}
        item={item}
        auctionStatus={auctionStatus}
        texts={{
          preSubmission: texts.preSubmission,
          liveView: texts.liveView,
          liveBid: texts.liveBid,
          auctionEnded: texts.auctionEnded,
          liveStartPrice: texts.liveStartPrice,
          startPrice: texts.startPrice,
          cancelBid: texts.cancelBid
        }}
        exchangeRate={exchangeRate}
        formatLiveTime={formatLiveTime}
        formatPrice={formatPrice}
        formatLiveEndTime={formatLiveEndTime}
        onCardClick={handleCardClick}
        onFavoriteToggle={handleFavoriteToggle}
        // 사전입찰을 한 경우이고 사전입찰 기간이면 입찰 취소 버튼 표시
        isBidHistory={userHasPreBid && auctionStatus === 'submission-period'}
        onCancelBid={handleCancelBid}
        isTopPreBidder={isTopPreBidder}
        topPreBidderLabel="Winning"
        isSecondPreBidder={isSecondPreBidder}
        secondPreBidderLabel="Loosing"
      />
    );
  };

  const renderContent = () => {
    if (!isLoggedIn) {
      return (
        <NoDataContainer 
          message="로그인이 필요합니다"
          imageSize={107}
        />
      );
    }

    if (loading) {
      return (
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 120px)', /* 앱바 높이를 제외한 전체 화면 높이 */
          width: '100%'
        }}>
          <DataLoader />
        </div>
      );
    }

    if (error) {
      return (
        <NoDataContainer 
          message={error}
          imageSize={107}
        />
      );
    }

    if (favoriteItems.length === 0) {
      return (
        <NoDataContainer 
          message={texts.noFavoritesMessage}
          imageSize={107}
        />
      );
    }

    return (
      <FavoritesGrid>
        {favoriteItems.map(renderFavoriteCard)}
      </FavoritesGrid>
    );
  };

  // 모바일 레이아웃
  const MobileLayout = (
    <MainLayout currentPage="favorites">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="favorites"
          title={texts.title}
        />
        <FavoritesContainer>
          <ContentContainer>
            {renderContent()}
          </ContentContainer>
        </FavoritesContainer>
        
        {/* 모달 */}
        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          header={modalState.title}
          body={modalState.message}
          footer={
            <PrimaryButton onClick={closeModal}>
              {modalState.buttonText}
            </PrimaryButton>
          }
        />
        
        {/* 입찰 취소 모달 */}
        <Modal
          isOpen={cancelBidModalState.isOpen}
          onClose={handleCancelBidModalClose}
          header={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalTitle : texts.cancelBidBlockedTitle}
          bgColor="white"
          body={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalMessage : (cancelBidModalState.blockedReason || '')}
          footer={
            cancelBidModalState.mode === 'confirm' ? (
              <ButtonContainer>
                <CancelModalButton onClick={handleCancelBidModalClose}>
                  {texts.cancel}
                </CancelModalButton>
                <ConfirmModalButton onClick={handleConfirmCancelBid}>
                  {texts.confirmCancel}
                </ConfirmModalButton>
              </ButtonContainer>
            ) : (
              <ButtonContainer>
                <ConfirmModalButton onClick={handleCancelBidModalClose}>
                  {texts.confirmButton}
                </ConfirmModalButton>
              </ButtonContainer>
            )
          }
        />
      </CenteredLayout>
    </MainLayout>
  );

  // 데스크톱 레이아웃
  const DesktopLayout = (
    <MainLayout currentPage="favorites">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="favorites"
        />
        <FavoritesContainer>
          <ContentContainer>
            {renderContent()}
          </ContentContainer>
        </FavoritesContainer>
        
        {/* 모달 */}
        <Modal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          header={modalState.title}
          body={modalState.message}
          footer={
            <PrimaryButton onClick={closeModal}>
              {modalState.buttonText}
            </PrimaryButton>
          }
        />
        
        {/* 입찰 취소 모달 */}
        <Modal
          isOpen={cancelBidModalState.isOpen}
          onClose={handleCancelBidModalClose}
          header={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalTitle : texts.cancelBidBlockedTitle}
          bgColor="white"
          body={cancelBidModalState.mode === 'confirm' ? texts.cancelBidModalMessage : (cancelBidModalState.blockedReason || '')}
          footer={
            cancelBidModalState.mode === 'confirm' ? (
              <ButtonContainer>
                <CancelModalButton onClick={handleCancelBidModalClose}>
                  {texts.cancel}
                </CancelModalButton>
                <ConfirmModalButton onClick={handleConfirmCancelBid}>
                  {texts.confirmCancel}
                </ConfirmModalButton>
              </ButtonContainer>
            ) : (
              <ButtonContainer>
                <ConfirmModalButton onClick={handleCancelBidModalClose}>
                  {texts.confirmButton}
                </ConfirmModalButton>
              </ButtonContainer>
            )
          }
        />
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
}

export default Favorites;
